import { parsePublicQrInput } from "@mvk/shared";
import type { ScanTargetType } from "../types";

export interface ScanResult {
  targetType: ScanTargetType;
  targetId: string;
  codeType: "qr" | "barcode";
  codeValue: string;
}

const SCAN_TARGET_I18N: Record<
  string,
  { key: string; fallbackRu: string }
> = {
  company: { key: "leads.scan_type_company", fallbackRu: "Компания" },
  booth: { key: "leads.scan_type_booth", fallbackRu: "Стенд" },
  badge: { key: "leads.scan_type_badge", fallbackRu: "Бейдж" },
  representative: {
    key: "leads.scan_type_representative",
    fallbackRu: "Представитель",
  },
  product: { key: "leads.scan_type_product", fallbackRu: "Товар" },
  map_point: { key: "leads.scan_type_map_point", fallbackRu: "Точка на карте" },
  visitor: { key: "leads.scan_type_visitor", fallbackRu: "Посетитель" },
  session: { key: "leads.scan_type_session", fallbackRu: "Сессия" },
};

/**
 * Lightweight client-side code parsing (server resolves on POST /scans).
 * Also accepts public https://…/go/… URLs from printed QRs.
 */
export async function recognizeCode(code: string): Promise<ScanResult | null> {
  const trimmed = code.trim();
  if (!trimmed) return null;

  const fromPublic = parsePublicQrInput(trimmed);
  const opaque = fromPublic?.opaque ?? trimmed;

  if (opaque.startsWith("company:")) {
    const id = opaque.slice("company:".length);
    return { targetType: "company", targetId: id, codeType: "qr", codeValue: opaque };
  }

  if (opaque.startsWith("booth:")) {
    const parts = opaque.split(":");
    const companyId = parts[2] ?? parts[parts.length - 1];
    if (!companyId) return null;
    return { targetType: "booth", targetId: companyId, codeType: "qr", codeValue: opaque };
  }

  if (opaque.startsWith("badge:")) {
    const id = opaque.slice("badge:".length);
    return { targetType: "representative", targetId: id, codeType: "qr", codeValue: opaque };
  }

  if (opaque.startsWith("product:")) {
    const id = opaque.slice("product:".length);
    return { targetType: "product", targetId: id, codeType: "qr", codeValue: opaque };
  }

  return null;
}

/** Resolve scan target label via i18n `t`; falls back to RU strings. */
export function getScanTargetLabel(
  targetType: ScanTargetType | string,
  t?: (key: string) => string,
): string {
  const meta = SCAN_TARGET_I18N[targetType];
  if (!meta) {
    if (typeof targetType === "string" && targetType) return targetType;
    const unknownKey = "leads.scan_type_unknown";
    if (t) {
      const translated = t(unknownKey);
      if (translated && translated !== unknownKey) return translated;
    }
    return "Неизвестно";
  }
  if (t) {
    const translated = t(meta.key);
    if (translated && translated !== meta.key) return translated;
  }
  return meta.fallbackRu;
}
